// TravelAgent.js — Composants d'agent IA pour CroisièreAI.
//
//   ProjectDescriber  — champ texte sur la page welcome. L'utilisateur décrit
//                       son projet en une phrase, Claude Haiku extrait les
//                       paramètres et saute les étapes déjà renseignées.
//
//   OfferChat         — zone de chat contextualisée sur l'offre affichée dans
//                       results. Le prompt système ET le contexte d'offre sont
//                       mis en cache côté Anthropic (prompt caching) pour que
//                       les questions suivantes dans la même conversation
//                       soient ~90% moins chères.

import { useState, useRef, useEffect } from "react";
import { useLanguage } from "./useLanguage";
import { track } from "./track";
import { clampDatesToFuture } from "./App";

const GF = { fontFamily: "'Playfair Display', serif" };
const GL = { fontFamily: "Lato, sans-serif" };

// ─── Récap lisible des critères captés (construit côté client, bilingue) ──────
const REGION_LABELS = {
  carib_east:  { fr: "Caraïbes Est",  en: "Eastern Caribbean" },
  carib_west:  { fr: "Caraïbes Ouest", en: "Western Caribbean" },
  carib_south: { fr: "Caraïbes Sud",  en: "Southern Caribbean" },
  bermuda:     { fr: "Bermudes",      en: "Bermuda" },
  europe:      { fr: "Europe",        en: "Europe" },
  alaska:      { fr: "Alaska",        en: "Alaska" },
  hawaii:      { fr: "Hawaï",         en: "Hawaii" },
  asia:        { fr: "Asie",          en: "Asia" },
  any:         { fr: "peu importe",   en: "no preference" },
};
const MONTHS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function buildRecap(p, isEN) {
  if (!p) return [];
  const L = (fr, en) => (isEN ? en : fr);
  const rows = [];

  // Qui voyage
  const fd = p.family_detail;
  if (fd && fd.adults != null) {
    const kids = Array.isArray(fd.children) ? fd.children.length : 0;
    let v = `${fd.adults} ${L(fd.adults > 1 ? "adultes" : "adulte", fd.adults > 1 ? "adults" : "adult")}`;
    if (kids > 0) v += ` + ${kids} ${L(kids > 1 ? "enfants" : "enfant", kids > 1 ? "children" : "child")}`;
    rows.push([L("Voyageurs", "Travellers"), v]);
  } else if (p.group) {
    const g = { solo: L("1 voyageur", "1 traveller"), couple: L("2 personnes", "2 people"),
                family: L("famille", "family"), group: L("groupe", "group") }[p.group];
    if (g) rows.push([L("Voyageurs", "Travellers"), p.group_size ? `${g} (${p.group_size})` : g]);
  }

  // Région
  if (Array.isArray(p.regions) && p.regions.length) {
    rows.push([L("Destination", "Destination"),
      p.regions.map(r => (REGION_LABELS[r] ? REGION_LABELS[r][isEN ? "en" : "fr"] : r)).join(", ")]);
  }

  // Quand
  const d = p.dates || {};
  if (d.from && d.to) {
    rows.push([L("Quand", "When"), L(`du ${d.from} au ${d.to}`, `${d.from} → ${d.to}`)]);
  } else if (d.from) {
    rows.push([L("Quand", "When"), L(`départ ${d.from}`, `departing ${d.from}`)]);
  } else if (d.month != null) {
    // Convention 1-12 (janvier=1) → index 0-based : month - 1 (corrige l'affichage décalé).
    const m = (isEN ? MONTHS_EN : MONTHS_FR)[d.month - 1] || "";
    rows.push([L("Quand", "When"), `${m}${d.year ? " " + d.year : ""}`.trim()]);
  } else if (p.when === "anytime") {
    rows.push([L("Quand", "When"), L("dates flexibles", "flexible dates")]);
  }

  // Durée (affiché si capté — sinon l'usager croyait que l'agent l'avait perdue)
  if (typeof p.exact_nights === "number" && p.exact_nights >= 2) {
    rows.push([L("Durée", "Duration"), `${p.exact_nights} ${L("nuits", "nights")}`]);
  } else if (p.duration) {
    const dur = {
      short: L("3 à 6 nuits", "3-6 nights"), week: L("7 nuits", "7 nights"),
      long:  L("8 nuits et +", "8+ nights"), xlong: L("15 nuits et +", "15+ nights"),
      any:   L("peu importe", "any length"),
    }[p.duration];
    if (dur) rows.push([L("Durée", "Duration"), dur]);
  }

  // Budget
  if (typeof p.budget === "number" && p.budget > 0) {
    rows.push([L("Budget", "Budget"), `${p.budget.toLocaleString(isEN ? "en-CA" : "fr-CA")} $ CAD`]);
  }

  // Cabine (affiché si capté)
  if (p.cabin) {
    const cab = {
      balcony_ocean: L("balcon vue mer", "ocean-view balcony"),
      balcony_centralpark: L("balcon Central Park", "Central Park balcony"),
      balcony_boardwalk: L("balcon Boardwalk", "Boardwalk balcony"),
      oceanview: L("vue mer", "ocean view"), inside: L("intérieure", "interior"),
      suite: L("suite", "suite"),
    }[p.cabin];
    if (cab) rows.push([L("Cabine", "Cabin"), cab]);
  }

  // Départ
  if (p.departure) {
    const origin = p.origin_airport === "other"
      ? (p.origin_airport_custom || "")
      : ({ YUL: L("Montréal", "Montreal"), YQB: L("Québec", "Quebec City") }[p.origin_airport] || "");
    const dep = {
      drive_ny: L("voiture vers New York", "driving to New York"),
      fly_south: L("vol", "flight"),
      either:   L("vol ou voiture", "flight or driving"),
    }[p.departure];
    if (dep) rows.push([L("Transport", "Getting there"), origin && p.departure !== "drive_ny" ? `${dep} — ${origin}` : dep]);
  }

  // Expérience (bonus utile)
  if (p.experience) {
    const e = { first: L("première croisière", "first cruise"),
                some: L("déjà croisé", "cruised before"),
                expert: L("habitué", "frequent cruiser") }[p.experience];
    if (e) rows.push([L("Expérience", "Experience"), e]);
  }

  // Compagnie / navire précis (bonus si mentionné)
  if (p.specific_ship) rows.push([L("Navire", "Ship"), p.specific_ship]);
  else if (Array.isArray(p.companies) && p.companies.length) {
    rows.push([L("Compagnie", "Cruise line"),
      p.companies.map(c => c.replace(/[-_]/g, " ").replace(/\b\w/g, m => m.toUpperCase())).join(", ")]);
  }

  // Classe de navire (si l'usager a précisé une classe : Icon, Oasis…)
  if (Array.isArray(p.ship_class) && p.ship_class.length) {
    rows.push([L("Classe de navire", "Ship class"),
      p.ship_class.map(c => c.replace(/_class$/, "").replace(/[-_]/g, " ").replace(/\b\w/g, m => m.toUpperCase())).join(", ")]);
  }

  // Priorités de classement (capté presque toujours — c'est le critère #1 de tri)
  if (Array.isArray(p.priorities) && p.priorities.length) {
    const pl = { prix: L("prix", "price"), itineraire: L("itinéraire", "itinerary"),
                 bateau: L("bateau", "ship"), cabine: L("cabine", "cabin") };
    rows.push([L("Priorités", "Priorities"), p.priorities.map(x => pl[x] || x).join(" › ")]);
  }

  return rows;
}

// ─── PROJECT DESCRIBER (agent conversationnel pré-funnel sur welcome) ─────────
export function ProjectDescriber({ workerUrl, onExtracted, onSkipToForm }) {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]); // { role, content }
  const [input, setInput]       = useState("");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const [recapParams, setRecapParams] = useState(null); // non-null = agent a finalisé
  const scrollRef = useRef(null);
  const { lang } = useLanguage();
  const isEN = lang === "en";

  // Persistance conversation : l'inscription Clerk recharge la page → sans ça
  // tout le texte tapé dans l'agent était perdu (GRAND irritant signalé).
  // On sauve au fil de l'eau et on restaure au remontage (fenêtre 60 min).
  const PD_KEY = "cai_agent_convo";
  const clearConvo = () => { try { localStorage.removeItem(PD_KEY); } catch {} };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PD_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (!s || Date.now() - (s.ts || 0) > 60 * 60 * 1000) { clearConvo(); return; }
      if (Array.isArray(s.messages) && s.messages.length > 0) {
        setMessages(s.messages);
        setRecapParams(s.recapParams || null);
        setInput(s.input || "");
        setOpen(true); // rouvre l'agent là où l'utilisateur était
      }
    } catch {}
  }, []); // au montage uniquement

  useEffect(() => {
    try {
      if (messages.length > 0 || input.trim()) {
        localStorage.setItem(PD_KEY, JSON.stringify({
          messages, input, recapParams, ts: Date.now(),
        }));
      }
    } catch {}
  }, [messages, input, recapParams]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, recapParams]);

  const conversationText = () =>
    messages.filter(m => m.role === "user").map(m => m.content).join(" ");

  const send = async () => {
    const t = input.trim();
    if (!t || busy || recapParams) return;
    const next = [...messages, { role: "user", content: t }];
    setMessages(next);
    setInput("");
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(workerUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "agent", messages: next, lang: isEN ? "en" : "fr" }),
      });
      const json = await res.json();
      if (!res.ok) {
        const e = json.error || {};
        throw new Error(e.code ? `[${e.code}] ${e.message}` : (e.message || `HTTP ${res.status}`));
      }
      if (json.ready) {
        // Garde-fou date : l'agent peut renvoyer une année passée (biais « 2025 ») →
        // on l'avance au futur AVANT d'afficher le recap et de lancer la recherche.
        const rp = json.params || {};
        if (rp.dates) rp.dates = clampDatesToFuture(rp.dates);
        setRecapParams(rp);
        setMessages([...next, {
          role: "assistant",
          content: isEN
            ? "Perfect — here's what I understood. Ready to search?"
            : "Parfait — voici ce que j'ai compris. On lance la recherche ?",
        }]);
      } else {
        setMessages([...next, { role: "assistant", content: json.reply || "…" }]);
      }
    } catch (e) {
      setError(e.message || (isEN ? "Unexpected error" : "Erreur inattendue"));
    } finally {
      setBusy(false);
    }
  };

  // ── Carte repliée (welcome) ────────────────────────────────────────────────
  if (!open) {
    return (
      <div style={{
        background: "#091929", border: "1px solid #C9A84C55",
        borderRadius: 16, overflow: "hidden", marginBottom: 12,
        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      }}>
        <div style={{
          padding: "10px 14px", borderBottom: "1px solid #1A2F45",
          display: "flex", alignItems: "center", gap: 10,
          background: "linear-gradient(180deg,rgba(201,168,76,0.08),transparent)",
        }}>
          <div style={{ fontSize: 16 }}>🧳</div>
          <div style={{ flex: 1 }}>
            <div style={{ ...GF, fontSize: 15, color: "#C9A84C", fontWeight: 700 }}>
              {isEN ? "AI Travel Agent" : "Agent de voyage IA"}
            </div>
            <div style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>
              {isEN ? "Online · responds instantly" : "En ligne · répond instantanément"}
            </div>
          </div>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#5CB85C", boxShadow: "0 0 6px #5CB85C" }} />
        </div>
        <div style={{ padding: "11px 14px 12px" }}>
          <div style={{
            ...GL, fontSize: 14, color: "#A8C0D0", lineHeight: 1.7,
            background: "rgba(255,255,255,0.03)", padding: "10px 13px",
            borderRadius: 12, border: "1px solid rgba(255,255,255,0.05)", marginBottom: 10,
          }}>
            {isEN
              ? "Hello! 👋 Tell me about your cruise plans — who's going, where, when, budget, what matters to you. The more you tell me, the fewer questions I'll have."
              : "Bonjour ! 👋 Racontez-moi votre projet de croisière — qui part, où, quand, le budget, ce qui compte pour vous. Plus vous m'en dites, moins j'aurai de questions."}
          </div>
          <button onClick={() => { track("welcome_agent_click"); setOpen(true); }} style={{
            width: "100%", padding: "10px 14px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#C9A84C,#A8882A)",
            color: "#07111F", ...GL, fontWeight: 900, fontSize: 15,
            cursor: "pointer", textAlign: "center",
          }}>
            {isEN ? "✨ Describe my trip →" : "✨ Décrire mon projet →"}
          </button>
        </div>
      </div>
    );
  }

  const recapRows = recapParams ? buildRecap(recapParams, isEN) : [];
  const canSend = input.trim().length > 0 && !busy && !recapParams;

  // ── Chat ouvert ────────────────────────────────────────────────────────────
  return (
    <div style={{
      background: "#091929", border: "1px solid #C9A84C55",
      borderRadius: 16, overflow: "hidden", marginBottom: 12,
      boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
    }}>
      <div style={{
        padding: "11px 14px", borderBottom: "1px solid #1A2F45",
        display: "flex", alignItems: "center", gap: 10,
        background: "linear-gradient(180deg,rgba(201,168,76,0.08),transparent)",
      }}>
        <div style={{ fontSize: 18 }}>🧳</div>
        <div style={{ flex: 1 }}>
          <div style={{ ...GF, fontSize: 14, color: "#C9A84C", fontWeight: 700 }}>
            {isEN ? "AI Travel Agent" : "Agent de voyage IA"}
          </div>
          <div style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>
            {isEN ? "Online · responds instantly" : "En ligne · répond instantanément"}
          </div>
        </div>
        <button onClick={() => { setOpen(false); setMessages([]); setInput(""); setError(null); setRecapParams(null); }} style={{
          background: "transparent", border: "1px solid #1A2F45",
          color: "#5A7A95", borderRadius: 8, padding: "4px 10px",
          ...GL, fontSize: 13, cursor: "pointer",
        }}>
          {isEN ? "Close" : "Fermer"}
        </button>
      </div>

      <div ref={scrollRef} style={{
        maxHeight: 340, overflowY: "auto", padding: 14,
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        {messages.length === 0 && (
          <div style={{
            ...GL, fontSize: 14, color: "#A8C0D0", lineHeight: 1.7,
            background: "rgba(255,255,255,0.03)", padding: "11px 13px",
            borderRadius: 12, border: "1px solid rgba(255,255,255,0.05)",
          }}>
            {isEN
              ? "Tell me about your cruise plans — who's going, where, when, your budget, and how you'd get to the port (flight or driving). The more you share, the fewer questions I'll ask."
              : "Racontez-moi votre projet de croisière — qui part, où, quand, votre budget, et comment vous rejoindriez le port (avion ou voiture). Plus vous m'en dites, moins j'aurai de questions."}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "86%",
            background: m.role === "user"
              ? "linear-gradient(135deg,#C9A84C,#A8882A)"
              : "rgba(255,255,255,0.04)",
            color: m.role === "user" ? "#07111F" : "#C8D8E8",
            border: m.role === "user" ? "none" : "1px solid rgba(255,255,255,0.06)",
            borderRadius: 12, padding: "9px 12px",
            ...GL, fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap",
            fontWeight: m.role === "user" ? 700 : 400,
          }}>
            {m.content}
          </div>
        ))}

        {busy && (
          <div style={{
            alignSelf: "flex-start", background: "rgba(255,255,255,0.04)",
            color: "#7A9BB5", borderRadius: 12, padding: "9px 14px",
            ...GL, fontSize: 14, letterSpacing: 2,
          }}>
            <span style={{ animation: "ca-blink 1.2s infinite" }}>●</span>
            <span style={{ animation: "ca-blink 1.2s infinite 0.25s", marginLeft: 4 }}>●</span>
            <span style={{ animation: "ca-blink 1.2s infinite 0.5s", marginLeft: 4 }}>●</span>
          </div>
        )}

        {recapParams && (
          <div style={{
            alignSelf: "stretch", background: "rgba(201,168,76,0.07)",
            border: "1px solid #C9A84C44", borderRadius: 12, padding: "12px 13px",
          }}>
            <div style={{
              ...GL, fontSize: 11, color: "#C9A84C", fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 9,
            }}>
              ✨ {isEN ? "Here's what I understood" : "Voici ce que j'ai compris"}
            </div>
            {recapRows.length === 0 && (
              <div style={{ ...GL, fontSize: 13, color: "#7A9BB5", marginBottom: 8 }}>
                {isEN ? "Not many details yet — the search will stay broad." : "Peu de détails pour l'instant — la recherche restera large."}
              </div>
            )}
            {recapRows.map(([label, value], i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <div style={{ ...GL, fontSize: 13, color: "#5A7A95", minWidth: 88 }}>{label}</div>
                <div style={{ ...GL, fontSize: 13, color: "#C8D8E8", fontWeight: 700, flex: 1 }}>{value}</div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                onClick={() => { clearConvo(); onExtracted(recapParams, conversationText(), { launch: false }); }}
                style={{
                  flex: 1, padding: "10px", borderRadius: 10,
                  border: "1px solid #1A2F45", background: "transparent",
                  color: "#A8C0D0", ...GL, fontSize: 14, cursor: "pointer",
                }}>
                {isEN ? "Adjust in form" : "Ajuster dans le formulaire"}
              </button>
              <button
                onClick={() => onExtracted(recapParams, conversationText(), { launch: true })}
                style={{
                  flex: 2, padding: "10px", borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg,#C9A84C,#A8882A)",
                  color: "#07111F", ...GL, fontSize: 14, fontWeight: 900, cursor: "pointer",
                }}>
                {isEN ? "🔎 Search now" : "🔎 Lancer la recherche"}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div style={{
            ...GL, fontSize: 13, color: "#E8524A", padding: "6px 10px",
            background: "#E8524A11", borderRadius: 8,
          }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {!recapParams && (
        <div style={{ padding: 12, borderTop: "1px solid #1A2F45", display: "flex", gap: 8 }}>
          <input
            autoFocus
            placeholder={isEN
              ? "E.g. 2 of us, Caribbean, mid-November 2026, ~$3,000 CAD…"
              : "Ex: on est 2, Caraïbes, mi-novembre 2026, ~3000 $ CAD…"}
            value={input}
            maxLength={1500}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            disabled={busy}
            style={{
              flex: 1, background: "#0A1E35", border: "1px solid #1A2F45",
              borderRadius: 10, padding: "9px 12px", color: "#C8D8E8",
              ...GL, fontSize: 14, outline: "none",
            }}
          />
          <button onClick={send} disabled={!canSend} style={{
            padding: "9px 16px", borderRadius: 10, border: "none",
            background: canSend ? "linear-gradient(135deg,#C9A84C,#A8882A)" : "#1A2F45",
            color: canSend ? "#07111F" : "#3A5A72",
            ...GL, fontSize: 14, fontWeight: 900,
            cursor: canSend ? "pointer" : "not-allowed",
          }}>
            →
          </button>
        </div>
      )}

      <div style={{ textAlign: "center", padding: "0 12px 11px" }}>
        <button
          onClick={() => { clearConvo(); setOpen(false); onSkipToForm && onSkipToForm(); }}
          style={{
            background: "transparent", border: "none", color: "#3A5A72",
            ...GL, fontSize: 13, cursor: "pointer", textDecoration: "underline", padding: 0,
          }}>
          {isEN ? "I'd rather fill out a form" : "Je préfère remplir un formulaire"}
        </button>
      </div>

      <style>{`@keyframes ca-blink { 0%,80%,100% { opacity: 0.25 } 40% { opacity: 1 } }`}</style>
    </div>
  );
}

// ─── OFFER CHAT (sur results) ─────────────────────────────────────────────────
export function OfferChat({ workerUrl, offer, data, forceOpen, onForceOpenHandled, onRelaunch }) {
  const [open, setOpen]         = useState(false);
  const { lang } = useLanguage();
  const isEN = lang === "en";

  useEffect(() => {
    if (forceOpen) { setOpen(true); onForceOpenHandled?.(); }
  }, [forceOpen, onForceOpenHandled]);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const offerContext = offer ? JSON.stringify({
    ship_name:         offer.ship_name,
    ship_class:        offer.ship_class,
    cruise_line:       offer.cruise_line,
    catchTitle:        offer.catchTitle,
    dateLabel:         offer.dateLabel,
    port:              offer.port,
    itinerary_summary: offer.itinerary_summary,
    destinations:      offer.destinations,
    total_cad:         offer.total_cad,
    price_per_person:  offer.price_per_person,
    cabins:            offer.cabins,
    user_criteria: {
      experience: data?.experience,
      group:      data?.group,
      budget:     data?.budget,
      departure:  data?.departure,
    },
  }) : null;

  const send = async () => {
    const t = input.trim();
    if (!t || busy) return;
    const next = [...messages, { role: "user", content: t }];
    setMessages(next);
    setInput("");
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(workerUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action:       "chat",
          messages:     next,
          offerContext,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        const e = json.error || {};
        throw new Error(e.code ? `[${e.code}] ${e.message}` : (e.message || `HTTP ${res.status}`));
      }
      setMessages([...next, { role: "assistant", content: json.reply || "…" }]);
    } catch (e) {
      setError(e.message || (isEN ? "Unexpected error" : "Erreur inattendue"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div style={{
        marginTop: 18, background: "#091929", border: "1px solid #C9A84C55",
        borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      }}>
        <div style={{
          padding: "11px 14px", borderBottom: "1px solid #1A2F45",
          display: "flex", alignItems: "center", gap: 10,
          background: "linear-gradient(180deg,rgba(201,168,76,0.08),transparent)",
        }}>
          <div style={{ fontSize: 18 }}>🧳</div>
          <div style={{ flex: 1 }}>
            <div style={{ ...GF, fontSize: 15, color: "#C9A84C", fontWeight: 700 }}>
              {isEN ? "AI Travel Agent" : "Agent de voyage IA"}
            </div>
            <div style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>
              {isEN ? "Online · responds instantly" : "En ligne · répond instantanément"}
            </div>
          </div>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#5CB85C", boxShadow: "0 0 6px #5CB85C" }} />
        </div>
        <div style={{ padding: "12px 14px 10px" }}>
          <div style={{
            ...GL, fontSize: 14, color: "#A8C0D0", lineHeight: 1.7,
            background: "rgba(255,255,255,0.03)", padding: "10px 13px",
            borderRadius: 12, border: "1px solid rgba(255,255,255,0.05)",
            marginBottom: 10,
          }}>
            {isEN
              ? <>Hello! 👋 Interested in this cruise? Ask me anything — <strong style={{ color: "#C8D8E8" }}>ideal cabin, ports, logistics, budget</strong>… I'm here to help you decide with confidence.</>
              : <>Bonjour ! 👋 Je suis votre agent de voyage IA. Cette croisière vous intéresse ? Posez-moi n'importe quelle question — <strong style={{ color: "#C8D8E8" }}>cabine idéale, escales, logistique, budget</strong>… je suis là pour vous aider à décider.</>}
          </div>
          <button onClick={() => setOpen(true)} style={{
            width: "100%", padding: "10px 14px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#C9A84C,#A8882A)",
            color: "#07111F", ...GL, fontWeight: 900, fontSize: 15,
            cursor: "pointer", textAlign: "center",
          }}>
            {isEN ? "💬 Start conversation →" : "💬 Démarrer la conversation →"}
          </button>
        </div>
      </div>
    );
  }

  const canSend = input.trim().length > 0 && !busy;

  return (
    <div style={{
      marginTop: 18, background: "#091929", border: "1px solid #C9A84C55",
      borderRadius: 16, overflow: "hidden",
      boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
    }}>
      <div style={{
        padding: "11px 14px", borderBottom: "1px solid #1A2F45",
        display: "flex", alignItems: "center", gap: 10,
        background: "linear-gradient(180deg,rgba(201,168,76,0.08),transparent)",
      }}>
        <div style={{ fontSize: 18 }}>💬</div>
        <div style={{ flex: 1 }}>
          <div style={{ ...GF, fontSize: 14, color: "#C9A84C", fontWeight: 700 }}>
            {isEN ? "Travel Agent" : "Agent de voyage"}
          </div>
          <div style={{ ...GL, fontSize: 11, color: "#5A7A95" }}>
            {offer?.ship_name
              ? (isEN ? `About ${offer.ship_name}` : `À propos du ${offer.ship_name}`)
              : (isEN ? "Cruise advice" : "Conseils croisière")}
          </div>
        </div>
        <button onClick={() => setOpen(false)} style={{
          background: "transparent", border: "1px solid #1A2F45",
          color: "#5A7A95", borderRadius: 8, padding: "4px 10px",
          ...GL, fontSize: 13, cursor: "pointer",
        }}>
          {isEN ? "Close" : "Fermer"}
        </button>
      </div>

      <div ref={scrollRef} style={{
        maxHeight: 360, overflowY: "auto", padding: 14,
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        {messages.length === 0 && (
          <div style={{
            ...GL, fontSize: 14, color: "#A8C0D0", lineHeight: 1.7,
            background: "rgba(255,255,255,0.03)", padding: "11px 13px",
            borderRadius: 12, border: "1px solid rgba(255,255,255,0.05)",
          }}>
            {isEN
              ? "Hello! 👋 What aspect of this cruise interests you most? Destination, cabin choice, ports, flights, hotel… ask me anything, I'm here to help you decide with confidence."
              : "Bonjour ! 👋 Quel aspect de cette croisière vous intéresse le plus ? La destination, le choix de cabine, les escales, les vols, l'hôtel au port… posez-moi vos questions, je suis là pour vous aider à décider en toute confiance."}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "86%",
            background: m.role === "user"
              ? "linear-gradient(135deg,#C9A84C,#A8882A)"
              : "rgba(255,255,255,0.04)",
            color: m.role === "user" ? "#07111F" : "#C8D8E8",
            border: m.role === "user" ? "none" : "1px solid rgba(255,255,255,0.06)",
            borderRadius: 12, padding: "9px 12px",
            ...GL, fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap",
            fontWeight: m.role === "user" ? 700 : 400,
          }}>
            {m.content}
          </div>
        ))}

        {busy && (
          <div style={{
            alignSelf: "flex-start", background: "rgba(255,255,255,0.04)",
            color: "#7A9BB5", borderRadius: 12, padding: "9px 14px",
            ...GL, fontSize: 14, letterSpacing: 2,
          }}>
            <span style={{ animation: "ca-blink 1.2s infinite" }}>●</span>
            <span style={{ animation: "ca-blink 1.2s infinite 0.25s", marginLeft: 4 }}>●</span>
            <span style={{ animation: "ca-blink 1.2s infinite 0.5s", marginLeft: 4 }}>●</span>
          </div>
        )}

        {error && (
          <div style={{
            ...GL, fontSize: 13, color: "#E8524A", padding: "6px 10px",
            background: "#E8524A11", borderRadius: 8,
          }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {onRelaunch && (
        <div style={{
          padding: "10px 12px", borderTop: "1px solid #1A2F45",
          display: "flex", justifyContent: "center",
          background: "rgba(201,168,76,0.04)",
        }}>
          <button onClick={onRelaunch} style={{
            background: "transparent", border: "1px solid #C9A84C66", borderRadius: 10,
            padding: "8px 16px", color: "#C9A84C", ...GL, fontSize: 13, cursor: "pointer",
            fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            🔄 {isEN ? "Re-run search with new criteria" : "Relancer ma recherche avec d'autres critères"}
          </button>
        </div>
      )}

      <div style={{
        padding: 12, borderTop: "1px solid #1A2F45", display: "flex", gap: 8,
      }}>
        <input
          placeholder={isEN ? "E.g. Which cabin would you recommend?" : "Ex: Quelle cabine me recommandez-vous ?"}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={busy}
          style={{
            flex: 1, background: "#0A1E35", border: "1px solid #1A2F45",
            borderRadius: 10, padding: "9px 12px", color: "#C8D8E8",
            ...GL, fontSize: 14, outline: "none",
          }}
        />
        <button onClick={send} disabled={!canSend} style={{
          padding: "9px 16px", borderRadius: 10, border: "none",
          background: canSend ? "linear-gradient(135deg,#C9A84C,#A8882A)" : "#1A2F45",
          color: canSend ? "#07111F" : "#3A5A72",
          ...GL, fontSize: 14, fontWeight: 900,
          cursor: canSend ? "pointer" : "not-allowed",
        }}>
          →
        </button>
      </div>

      <style>{`@keyframes ca-blink { 0%,80%,100% { opacity: 0.25 } 40% { opacity: 1 } }`}</style>
    </div>
  );
}
